import { useState, useEffect, useRef } from 'react';
import { LogisticNetwork, Hub, Connection, FileType } from '@/types/data';
import { useNetworkContext } from '@/contexts/NetworkContext';
import Papa from 'papaparse';

// Simple ID generation function as a replacement for uuid
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const useNetworks = () => {
  const [networks, setNetworks] = useState<LogisticNetwork[]>([]);
  const [newNetworkName, setNewNetworkName] = useState('');
  const { currentNetwork, setIsNetworkModified } = useNetworkContext();

  // Load networks from local storage on component mount
  useEffect(() => {
    // Only load from localStorage if networks array is empty
    // This prevents StrictMode from resetting the state on remount
    if (networks.length === 0) {
      const storedNetworks = localStorage.getItem('logisticNetworks');
      if (storedNetworks) {
        try {
          setNetworks(JSON.parse(storedNetworks));
        } catch (error) {
          console.error('Failed to parse networks from local storage:', error);
        }
      }
    }
  }, [networks.length]);

  // Save networks to local storage whenever they change (with debounce)
  const saveTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout to implement debouncing
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set a new timeout to save after 500ms of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem('logisticNetworks', JSON.stringify(networks));
    }, 500);

    // Cleanup function to clear timeout if component unmounts or networks changes again
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [networks]);

  const addNetwork = () => {
    if (newNetworkName.trim()) {
      const newNetwork: LogisticNetwork = {
        id: generateId(),
        name: newNetworkName.trim(),
        files: [],
      };
      setNetworks([...networks, newNetwork]);
      setNewNetworkName('');
      return newNetwork.id; // Return the new network ID for auto-expansion
    }
    return null;
  };

  const deleteNetwork = (networkId: string) => {
    if (confirm('Are you sure you want to delete this network and all its files?')) {
      setNetworks(networks.filter((network) => network.id !== networkId));
      return true;
    }
    return false;
  };

  const deleteFile = (networkId: string, fileId: string) => {
    if (confirm('Are you sure you want to delete this file?')) {
      setNetworks(
        networks.map((network) => {
          if (network.id === networkId) {
            return {
              ...network,
              files: network.files.filter((file) => file.id !== fileId),
            };
          }
          return network;
        })
      );

      // If the current network is being modified, set the modified flag
      if (currentNetwork && currentNetwork.id === networkId) {
        setIsNetworkModified(true);
      }

      return true;
    }
    return false;
  };

  const addFileToNetwork = (networkId: string, file: Omit<LogisticNetwork['files'][0], 'id'>) => {
    const updatedNetworks = [...networks];
    const networkIndex = updatedNetworks.findIndex((n) => n.id === networkId);

    if (networkIndex === -1) return false;

    const newFile = {
      ...file,
      id: generateId(),
    };

    updatedNetworks[networkIndex].files.push(newFile);
    setNetworks([...updatedNetworks]);

    // If the current network is being modified, set the modified flag
    if (currentNetwork && currentNetwork.id === networkId) {
      setIsNetworkModified(true);
    }

    return true;
  };

  // Generic function to save edited data to local storage
  const saveEditedData = (dataType: string, networkId: string, fileId: string, data: string) => {
    const storageKey = `edited_${dataType}_${networkId}_${fileId}`;
    localStorage.setItem(storageKey, data);
    return true;
  };

  // Generic function to load edited data from local storage
  const getEditedData = (dataType: string, networkId: string, fileId: string) => {
    const storageKey = `edited_${dataType}_${networkId}_${fileId}`;
    return localStorage.getItem(storageKey);
  };

  // Save edited hub data to local storage
  const saveEditedHubs = (networkId: string, fileId: string, hubsData: string) => {
    return saveEditedData('hubs', networkId, fileId, hubsData);
  };

  // Load edited hub data from local storage
  const getEditedHubs = (networkId: string, fileId: string) => {
    return getEditedData('hubs', networkId, fileId);
  };

  // Save edited connections data to local storage
  const saveEditedConnections = (networkId: string, fileId: string, connectionsData: string) => {
    return saveEditedData('connections', networkId, fileId, connectionsData);
  };

  // Load edited connections data from local storage
  const getEditedConnections = (networkId: string, fileId: string) => {
    return getEditedData('connections', networkId, fileId);
  };

  // Save edited serviceability data to local storage
  const saveEditedServiceability = (
    networkId: string,
    fileId: string,
    serviceabilityData: string
  ) => {
    return saveEditedData('serviceability', networkId, fileId, serviceabilityData);
  };

  // Load edited serviceability data from local storage
  const getEditedServiceability = (networkId: string, fileId: string) => {
    return getEditedData('serviceability', networkId, fileId);
  };

  // Generic autosave function
  const autosaveData = <T>(
    currentNetwork: LogisticNetwork,
    data: T[],
    fileType: FileType,
    saveFunction: (networkId: string, fileId: string, data: string) => boolean,
    isEdited: boolean,
    setIsSaving: (isSaving: boolean) => void
  ) => {
    if (!currentNetwork || !isEdited) return false;

    setIsSaving(true);

    // Convert data to CSV
    const csv = Papa.unparse(data);

    // Find the file
    const file = currentNetwork.files.find((file) => file.type === fileType);

    if (file) {
      // Save to local storage
      saveFunction(currentNetwork.id, file.id, csv);

      // Update UI state after a short delay to show the saving indicator
      setTimeout(() => {
        setIsSaving(false);
      }, 500);

      return true;
    } else {
      setIsSaving(false);
      return false;
    }
  };

  // Autosave connections data
  const autosaveConnections = (
    currentNetwork: LogisticNetwork,
    connections: Connection[],
    isEdited: boolean,
    setIsSaving: (isSaving: boolean) => void
  ) => {
    return autosaveData(
      currentNetwork,
      connections,
      FileType.Connections,
      saveEditedConnections,
      isEdited,
      setIsSaving
    );
  };

  // Autosave hubs data
  const autosaveHubs = (
    currentNetwork: LogisticNetwork,
    hubs: Hub[],
    isEdited: boolean,
    setIsSaving: (isSaving: boolean) => void
  ) => {
    return autosaveData(currentNetwork, hubs, FileType.Hubs, saveEditedHubs, isEdited, setIsSaving);
  };

  // Autosave serviceability data
  const autosaveServiceability = (
    currentNetwork: LogisticNetwork,
    serviceability: any[],
    isEdited: boolean,
    setIsSaving: (isSaving: boolean) => void
  ) => {
    return autosaveData(
      currentNetwork,
      serviceability,
      FileType.Serviceability,
      saveEditedServiceability,
      isEdited,
      setIsSaving
    );
  };

  return {
    networks,
    newNetworkName,
    setNewNetworkName,
    addNetwork,
    deleteNetwork,
    deleteFile,
    addFileToNetwork,
    generateId,
    saveEditedHubs,
    getEditedHubs,
    saveEditedConnections,
    getEditedConnections,
    saveEditedServiceability,
    getEditedServiceability,
    autosaveConnections,
    autosaveHubs,
    autosaveServiceability,
    getEditedData,
  };
};
